import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useUser } from "../../../../context/UserContext";
import { getTeamResources, inviteUserAsProviderResource } from "../../../../services/staffingService";
import DashboardSidebar from "../agencyDashboardSidebar";
import {
  Shield, Search, Plus, ChevronLeft, ChevronRight,
  MapPin, Mail, Phone, ChevronRight as Arrow, CheckCircle2, XCircle,
  UserPlus,
} from "lucide-react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

const ITEMS_PER_PAGE = 20;

const statusBadge = (officer) => {
  const active = officer.user_id?.status !== "inactive";
  return active ? (
    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
      Active
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
      Inactive
    </span>
  );
};

export default function OfficerList() {
  const { providerCompanyId } = useParams();
  const history = useHistory();
  const { userInfo } = useUser();

  const [showSidebar, setShowSidebar]     = useState(false);
  const [officers, setOfficers]           = useState([]);
  const [total, setTotal]                 = useState(0);
  const [page, setPage]                   = useState(0);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [inviteEmail, setInviteEmail]     = useState("");
  const [inviting, setInviting]           = useState(false);
  const [showInvite, setShowInvite]       = useState(false);
  const debounceRef = useRef(null);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(value);
    }, 350);
  }, []);

  const fetchOfficers = useCallback(async () => {
    if (!providerCompanyId) return;
    setLoading(true);
    try {
      const resp = await getTeamResources(providerCompanyId);
      setOfficers(resp.data.providerResources || []);
      setTotal((resp.data.providerResources || []).length);
    } catch (err) {
      console.error("Failed to fetch officers:", err);
    } finally {
      setLoading(false);
    }
  }, [providerCompanyId]);

  useEffect(() => { fetchOfficers(); }, [fetchOfficers]);

  // Client-side filter + paginate
  const filtered = officers.filter((o) => {
    if (!debouncedSearch) return true;
    const q = debouncedSearch.toLowerCase();
    const name = `${o.user_id?.firstName || ""} ${o.user_id?.lastName || ""}`.toLowerCase();
    return (
      name.includes(q) ||
      (o.user_id?.email || "").toLowerCase().includes(q) ||
      (o.address?.city || "").toLowerCase().includes(q) ||
      (o.address?.state || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const pageItems  = filtered.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await inviteUserAsProviderResource(providerCompanyId, inviteEmail.trim());
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
      setShowInvite(false);
      fetchOfficers();
    } catch (err) {
      toast.error("Could not send invite. Please try again.");
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Officers</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {loading ? "Loading…" : `${filtered.length} officer${filtered.length !== 1 ? "s" : ""} on your roster`}
              </p>
            </div>
            <button
              onClick={() => setShowInvite(true)}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary/90"
            >
              <Plus size={16} />
              Invite Officer
            </button>
          </div>

          {/* Invite modal */}
          {showInvite && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl">
                <h3 className="mb-4 text-base font-semibold text-card-foreground">Invite Officer</h3>
                <form onSubmit={handleInvite} className="space-y-3">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="officer@email.com"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-pink"
                    autoFocus
                    required
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowInvite(false)}
                      className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={inviting}
                      className="flex items-center gap-2 rounded-lg bg-brand-pink px-4 py-2 text-sm font-medium text-white hover:bg-brand-pink/90 disabled:opacity-50"
                    >
                      <UserPlus size={14} />
                      {inviting ? "Sending…" : "Send Invite"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="mb-4">
            <div className="relative max-w-sm">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name, email, city…"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-pink"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {["Officer", "Location", "Contact", "Services", "Status", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 6 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                        No officers found.
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((officer, index) => {
                      const firstName = officer.user_id?.firstName || "";
                      const lastName  = officer.user_id?.lastName  || "";
                      const fullName  = `${firstName} ${lastName}`.trim() || officer.user_id?.email || "Unknown";
                      const initials  = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "?";
                      const services  = officer.servicesOffered || [];

                      return (
                        <motion.tr
                          key={officer._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: index * 0.02 }}
                          className="cursor-pointer transition-colors hover:bg-muted/30"
                          onClick={() =>
                            history.push(`/provider-company/${providerCompanyId}/orm/${officer._id}`)
                          }
                        >
                          {/* Officer */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-blue-light">
                                {officer.user_id?.profilePictureUrl ? (
                                  <img
                                    src={officer.user_id.profilePictureUrl}
                                    alt={fullName}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span className="text-xs font-semibold text-brand-blue-dark">
                                    {initials}
                                  </span>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-card-foreground">
                                  {fullName}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {officer.user_id?.email || "—"}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Location */}
                          <td className="px-4 py-3">
                            {officer.address?.city ? (
                              <div className="flex items-start gap-1.5">
                                <MapPin size={13} className="mt-0.5 shrink-0 text-muted-foreground" />
                                <div>
                                  <p className="text-sm text-card-foreground">{officer.address.city}</p>
                                  {officer.address.state && (
                                    <p className="text-xs text-muted-foreground">{officer.address.state}</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </td>

                          {/* Contact */}
                          <td className="px-4 py-3">
                            <div className="space-y-0.5">
                              {officer.phoneNumber && (
                                <div className="flex items-center gap-1.5">
                                  <Phone size={11} className="text-muted-foreground" />
                                  <span className="text-xs text-card-foreground">{officer.phoneNumber}</span>
                                </div>
                              )}
                              {officer.user_id?.email && (
                                <div className="flex items-center gap-1.5">
                                  <Mail size={11} className="text-muted-foreground" />
                                  <span className="max-w-[160px] truncate text-xs text-card-foreground">
                                    {officer.user_id.email}
                                  </span>
                                </div>
                              )}
                              {!officer.phoneNumber && !officer.user_id?.email && (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </div>
                          </td>

                          {/* Services */}
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {services.length > 0 ? (
                                services.slice(0, 2).map((s) => (
                                  <span
                                    key={s}
                                    className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary"
                                  >
                                    {s}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                              {services.length > 2 && (
                                <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                  +{services.length - 2}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3">{statusBadge(officer)}</td>

                          {/* Arrow */}
                          <td className="px-2 py-3">
                            <Arrow size={14} className="text-muted-foreground" />
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {page * ITEMS_PER_PAGE + 1}–
                {Math.min((page + 1) * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-40"
                >
                  <ChevronLeft size={14} /> Back
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-40"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
